import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCart from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import {
    getCategoriesDataView,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import {
    getLink,
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import { MappedLink } from "@insite/mobius/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@insite/mobius/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaMyAccountPagesNavEvent,
} from "@stoneage/App/Utilities/DataLayer";
import { styledScrollbar } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinkModel[]) ?? [];
    const displayPricingAndInventory = state.context.session?.displayPricingAndInventory || false;
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const isCurrencyWidgetAuthored = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/CurrencyMenu";
    })?.fields;
    const isLanguageWidgetAuthored = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/LanguageMenu";
    })?.fields;
    const headerSignInFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Common/HeaderSignIn";
        })?.fields.links as LinkModel[]) ?? [];
    const { fulfillmentMethod, shipToId, pickUpWarehouse } = state.context.session;
    const shipTo = getShipToState(state, shipToId).value;
    const customSettings = getSettingsCollection(state).customSettings as any;

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        isSigningIn: state.context.isSigningIn,
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        vmiDashboardPageUrl: getPageLinkByPageType(state, "VmiDashboardPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        drawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        categoriesDataView: getCategoriesDataView(state),
        state,
        headerLinkListLinks: state.xHeader.headerLinkListLinks,
        headerSignInFields,
        fulfillmentMethod,
        shipTo,
        pickUpWarehouse,
        session: state.context.session,
        customSettings,
        isCurrencyWidgetAuthored,
        isLanguageWidgetAuthored,
        cart: getCurrentCartState(state).value,
        visibilityState: findVisibilityState(state.data.pages.widgetsById),
        displayPricingAndInventoryToggles: displayPricingAndInventory && currentMode === "Storefront",
        myAccountPagesLinks: {
            orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage")?.url,
            invoiceHistoryPageLink: getPageLinkByPageType(state, "InvoiceHistoryPage")?.url,
            myQuotesPageLink: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
            myProfilePageLink: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
            userAdministrationPageLink: getPageLinkByPageType(state, "UserListPage")?.url,
            sentinelPageLink: customSettings?.sentinelHeaderUrl,
            courseManagementPageLink: getPageLinkByPageType(state, "CourseManagementPage")?.url,
            myCoursesPageLink: getPageLinkByPageType(state, "MyCoursesPage")?.url,
        },
        customerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    displayVmiNavigation: boolean;
    vmiPageLinks: (PageLinkModel | undefined)[];
    displayModeSwitch?: boolean;
    currentMode?: string;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    rootElementSelector?: string;
    wideMenuTheme?: boolean;
    // allowOpenParentLinks?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

interface CustomLinkModel {
    url: string | undefined;
    title: string | undefined;
    overrideTitleL2?: string;
}

interface HeaderLinkListLink {
    link: CustomLinkModel;
    children: CustomLinkModel[];
    overrideTitle: string;
}

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    currencySelectorWrapper?: InjectableCss;
    languageSelectorWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    fulfillmentRow?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingToggle?: ShowPricingToggleStyles;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
    shipToTitleTypography?: TypographyPresentationProps;
    signOutIcon?: IconPresentationProps;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const navigationDrawerStyles: NavigationDrawerStyles = {
    drawer: {
        size: 480,
        cssOverrides: {
            drawerContent: css`
                overflow-x: hidden;
                overflow-y: auto;
                ${styledScrollbar(5)}
            `,
            scrim: css`
                top: 88px;
                > div > div {
                    top: 88px;
                    max-width: 480px;
                    width: 100%;
                    box-shadow: none;
                }
            `,
            drawerTitle: css`
                display: none;
            `,
        },
    },
    drawerSectionWrapper: {
        css: css`
            &:first-child {
                margin-top: 0;
                margin-bottom: -10px;
            }
            &:last-child {
            }
            margin-top: 20px;

            &.main-navigation-section {
                padding-top: 15px;
            }
        `,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css`
                > button {
                    width: 100%;
                }

                .fulfillment-row {
                    > span[class*="IconWrapper"] {
                        display: none;
                    }
                }
            `,
        },
        panelRowProps: {
            color: "common.white",
            css: css`
                background-color: ${getColor("common.white")};
                width: 100%;
                height: 42px;

                span:first-child {
                    font-size: 16px;
                    font-weight: bold;
                    line-height: 22px;
                    color: ${getColor("secondary")};
                    text-transform: capitalize;
                    width: calc(100% - 40px);
                }

                span[class*="IconWrapper"] {
                    color: ${getColor("common.ui6")};
                }
            `,
        },
    },
    menuRowIcon: {
        size: 18,
        css: css`
            margin-top: -2px;
            margin-left: -3px;
            margin-right: 5px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        transform: "uppercase",
        color: "common.background",
    },
    punchOutUserRowText: {
        transform: "uppercase",
        color: "common.background",
    },
    mainNavigationRow: {
        color: "common.white",
        css: css`
            margin: 0;
            .main-navigation-row-text {
                font-size: 16px;
                font-weight: bold;
                line-height: 22px;
                color: ${getColor("secondary")};
                text-transform: capitalize;
                width: calc(100% - 40px);
            }

            span[class*="IconWrapper"] {
                color: ${getColor("common.ui6")};
            }

            &.selector-menu span {
                display: flex;
                align-items: center;
            }
        `,
    },
    punchOutNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 18,
        css: css`
            margin-top: -2px;
            margin-left: -3px;
            margin-right: 5px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {
        transform: "uppercase",
        color: "common.background",
        weight: "bold",
    },
    fulfillmentRow: {
        color: "common.ui4",
        typographyProps: {
            ellipsis: true,
            transform: "uppercase",
        },
        css: css`
            margin: 0;
            height: 42px;
        `,
    },
    currencySymbol: {
        size: 18,
        css: css`
            margin-right: 2px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
            line-height: 1;
            height: 18px;
            text-align: left;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
    showInventoryToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    showPricingToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    signOutRow: {
        css: css`
            width: 100%;
        `,
    },
    signOutRowText: {
        transform: "uppercase",
        weight: "bold",
        css: css`
            margin-right: 8px;
        `,
    },
    signOutIcon: {
        size: 17,
        color: "primary",
        src: "IconSignout",
        css: css``,
    },
    shipToTitleTypography: {
        css: css`
            font-size: 12px;
            color: ${getColor("text.main")};
            font-weight: 500;

            &.shipTo_Pickup-value {
                color: ${getColor("text.main")};
            }
        `,
        ellipsis: true,
        transform: "capitalize",
    },
};

const styles = navigationDrawerStyles;

const NavigationDrawer: FC<Props> = props => {
    const dispatch = useDispatch();

    const closeDrawer = () => {
        props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => props.setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const onSignOutHandler = () => {
        props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        props.isPunchOutSession ? props.cancelPunchOut() : props.signOut();
    };

    const {
        links,
        currencies,
        currentCurrencyId,
        currentCurrencySymbol,
        languages,
        currentLanguage,
        setLanguage,
        setCurrency,
        userName,
        isGuest,
        myAccountPageLink,
        currentLocation,
        fulfillmentMethod,
        isPunchOutSession,
        headerLinkListLinkFields,
        history,
        displayVmiNavigation,
        displayModeSwitch,
        currentMode,
        rootElementSelector,
        vmiPageLinks,
        categoriesDataView,
        state,
        headerLinkListLinks,
        headerSignInFields,
        pickUpWarehouse,
        shipTo,
        showCustomerMenuItem,
        session,
        customSettings,
        isCurrencyWidgetAuthored,
        isLanguageWidgetAuthored,
        vmiDashboardPageUrl,
        cart,
        removeCart,
        myAccountPagesLinks,
        customerNumber,
        impersonation,
        wideMenuTheme,
        // allowOpenParentLinks,
    } = props;

    const navigationModeChangeHandler = (navigationMode: "Storefront" | "Vmi") => {
        setCookie(NavigationModeCookieName, navigationMode);
        if (navigationMode === "Vmi") {
            vmiDashboardPageUrl && history.push(vmiDashboardPageUrl);
        } else if (cart?.status === "AlternateCart") {
            removeCart({
                cartId: API_URL_CURRENT_FRAGMENT,
                isAlternateCart: true,
                onSuccess: () => {
                    props.homePageUrl && history.push(props.homePageUrl);
                },
            });
        } else {
            props.homePageUrl && history.push(props.homePageUrl);
        }
    };

    // setup Header Link List Links
    React.useEffect(() => {
        setupHeaderLinkListLinks();
    }, [headerLinkListLinkFields, myAccountPageLink]);

    const setupHeaderLinkListLinks = () => {
        const tempStateHeaderLinks: HeaderLinkListLink[] = [];
        // l1links
        const tempHeaderLinks: any = [];
        if (headerLinkListLinkFields && headerLinkListLinkFields.length) {
            for (let i = 0; i < headerLinkListLinkFields.length; i++) {
                const tCat = headerLinkListLinkFields[i];

                const {
                    fields: { destination, overriddenTitle, isMenu },
                } = tCat as any;

                let child: any = {};
                child = getLink(state, destination);
                child = { ...child, overriddenTitle, isMenu };

                tempHeaderLinks.push(child);
            }

            // l2 links
            for (let i = 0; i < tempHeaderLinks.length; i++) {
                const tCat = tempHeaderLinks[i];
                const link = { url: tCat.url, title: tCat.title };
                const overriddenTitle = tCat.overriddenTitle;
                const isMenu = tCat.isMenu;
                const children: any = [];
                const excludeFromNavigation = tCat?.excludeFromNavigation || false;

                if (
                    (headerLinkListLinkFields[i].fields as any).l2Links &&
                    (headerLinkListLinkFields[i].fields as any).l2Links.length > 0
                ) {
                    for (const link2 of (headerLinkListLinkFields[i].fields as any).l2Links) {
                        const {
                            fields: { destination, overriddenTitle },
                        } = link2;

                        let child: any = {};
                        child = getLink(state, destination);
                        child = { ...child, overriddenTitle };

                        children.push(child);
                    }
                }
                const catToPush: any = {
                    link,
                    children,
                    overriddenTitle,
                    excludeFromNavigation,
                    isMenu,
                };

                tempStateHeaderLinks.push(catToPush);
            }

            // store the header links in redux so that we use them here in this component and in the the HeaderLinkList component
            dispatch({ type: "xHeader/setHeaderLinkListLinks", headerLinkListLinks: tempStateHeaderLinks });
        }
    };

    // setup Main Navigation Links
    const [navLinks, setNavLinks] = React.useState([]);
    React.useEffect(() => {
        const tempLinks: any = [...(links as any)];
        tempLinks.forEach(
            (link: { isProductsMenu: string | any[]; title: string; linkType: string; children: string | any[] }) => {
                if (link.isProductsMenu && link.children?.length) {
                    // find real products menu from the tree
                    const productsMenu: any = (link.children as any).find(
                        (cat: { title: string }) => cat.title.toLowerCase() === "products",
                    );
                    if (!productsMenu || !productsMenu?.children?.length) {
                        link.children = link.children[0].children;
                        return;
                    }
                    link.children = productsMenu.children;
                }
            },
        );
        setNavLinks(tempLinks);
    }, [links]);

    // setup My Account Links
    const isSentinelUser = session?.properties?.isSentinelUser === "true" || false;
    const isThinkificUser = session?.properties?.isThinkificUser === "true" || false;
    const sentinelURL = customSettings?.sentinelHeaderUrl;
    const thinkificlURL = customSettings?.thinkificHeaderUrl;
    const [accountLinks, setAccountLinks] = React.useState([]);
    React.useEffect(() => {
        const tempLinks: any = [];
        for (let i = 0; i < headerSignInFields.length; i++) {
            const headerLink: any = headerSignInFields[i] as any;

            const {
                fields: { destination, overriddenTitle, openInNewTab },
            } = headerLink;

            const { type, value } = destination;

            let child = {};

            if (type === "Url") {
                child = {
                    url: value,
                    title: value,
                    overriddenTitle,
                    openInNewTab,
                };
            } else if (type === "Page") {
                const readonlyPageLink = getPageLinkByNodeId(state, value);
                if (readonlyPageLink) {
                    child = { ...readonlyPageLink, overriddenTitle, openInNewTab };
                }
            } else if (type === "Category") {
                const categoryState = getCategoryState(state, value);

                child = {
                    url: categoryState && categoryState.value ? categoryState.value.path : "",
                    title: categoryState && categoryState.value ? categoryState.value.shortDescription : "",
                    overriddenTitle,
                    openInNewTab,
                };
            }

            if ((child as any)?.url) {
                if (
                    ((child as any)?.url === sentinelURL && !isSentinelUser) ||
                    ((child as any)?.url === thinkificlURL && !isThinkificUser)
                ) {
                    continue;
                }
                tempLinks.push(child);
            }
        }
        setAccountLinks(tempLinks);
    }, [headerSignInFields]);

    const changeLanguage = (languageId: any) => {
        if (languages) {
            const selectedLanguage: any = languages?.find(lang => {
                return lang.id === languageId.languageId;
            });
            const param: any = {
                languageId: selectedLanguage.id,
                languageCode: selectedLanguage.languageCode,
                prevLanguageCode: currentLanguage?.languageCode,
            };
            setLanguage({ ...param });
        }
    };
    const currentPageUrl = currentLocation.pathname;

    const handlePageClick = (e: any, url?: string, text?: string) => {
        const { history } = props;

        if (!url) {
            return;
        }

        e.stopPropagation();
        e.preventDefault();

        // XNG-Change: track GA4
        trackGaMyAccountPagesNavEvent(null, url, text || "", impersonation, customerNumber, myAccountPagesLinks);

        closeDrawer();
        if (url.startsWith("http")) {
            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    return (
        <>
            <Drawer
                className="drawer"
                draggable
                position="left"
                {...styles.drawer}
                isOpen={props.drawerIsOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
                data-test-selector="drawerSection"
                rootElementSelector={rootElementSelector}
            >
                {/*  */}
                {/* Fulfillment Method */}
                {showCustomerMenuItem && (
                    <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                        <PanelMenu
                            className="panel-menu"
                            {...styles.panelMenu}
                            panelTrigger={
                                <PanelRow hasChildren className="fulfillment-row" {...styles.fulfillmentRow}>
                                    <StyledWrapper
                                        css={css`
                                            display: flex;
                                            align-items: center;
                                        `}
                                    >
                                        <Typography {...styles.shipToTitleTypography}>
                                            {translate("Fulfillment:")}&nbsp;
                                        </Typography>
                                        <Typography {...styles.shipToTitleTypography} className="shipTo_Pickup-value">
                                            {fulfillmentMethod === FulfillmentMethod.PickUp
                                                ? translate("Pickup at")
                                                : translate("Ship")}
                                            &nbsp;
                                        </Typography>
                                        <Typography {...styles.shipToTitleTypography} className="shipTo_Pickup-value">
                                            {fulfillmentMethod === FulfillmentMethod.PickUp
                                                ? pickUpWarehouse?.description
                                                : shipTo?.companyName}
                                            &nbsp;
                                        </Typography>
                                        <StyledWrapper
                                            {...styles.shipToTitleTypography}
                                            data-test-selector="header_shipToButton"
                                        >
                                            {translate("Change")}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </PanelRow>
                            }
                            layer={0}
                            closeOverlay={undefined}
                        >
                            {isPunchOutSession ? (
                                <PanelRow tabIndex={-1} className="change-customer-row" {...styles.changeCustomerRow}>
                                    {createWidgetElement("Header/AddressDrawerPunchOutCustomer", {
                                        fields: {},
                                    })}
                                </PanelRow>
                            ) : (
                                <PanelRow tabIndex={-1} className="change-customer-row" {...styles.changeCustomerRow}>
                                    <GridContainer
                                        className="change-customer-row-container"
                                        {...styles.changeCustomerRowContainer}
                                    >
                                        <GridItem
                                            className="fulfillment-method-grid-item"
                                            {...styles.fulfillmentMethodGridItem}
                                        >
                                            {createWidgetElement("Header/AddressDrawerFulfillmentMethodSelector", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem className="addresses-grid-item" {...styles.addressesGridItem}>
                                            {createWidgetElement("Header/AddressDrawerSelectCustomer", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem
                                            className="pick-up-address-grid-item"
                                            {...styles.pickUpAddressGridItem}
                                        >
                                            {createWidgetElement("Header/AddressDrawerPickUpLocationSelector", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem className="apply-button-grid-item" {...styles.applyButtonGridItem}>
                                            {createWidgetElement("Header/AddressDrawerApplyButton", { fields: {} })}
                                        </GridItem>
                                    </GridContainer>
                                </PanelRow>
                            )}
                        </PanelMenu>
                    </StyledSection>
                )}

                {/*  */}
                {/* covers MainNavigation functionality */}
                <StyledSection
                    className="drawer-section-wrapper main-navigation-section"
                    {...styles.drawerSectionWrapper}
                >
                    {!displayVmiNavigation &&
                        navLinks.map((link: any, index: any) => {
                            if (link.children && link.children.length > 0 && link.linkType !== "Link") {
                                return (
                                    <PanelMenu
                                        className="panel-menu"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        currentUrl={currentPageUrl}
                                        closeOverlay={closeDrawer}
                                        panelTrigger={
                                            <PanelRow
                                                className="main-navigation-row"
                                                hasChildren
                                                {...styles.mainNavigationRow}
                                            >
                                                <Typography
                                                    className="main-navigation-row-text"
                                                    onClick={(e: any) => handlePageClick(e, link.url, link.title)}
                                                    {...styles.mainNavigationRowTypography}
                                                >
                                                    {parse(link.title, parserOptions)}
                                                </Typography>
                                            </PanelRow>
                                        }
                                        menuItems={link.children}
                                        //  SAINC-1522: https://xngage.atlassian.net/browse/SAINC-1522
                                        maxDepth={(link as any).linkType !== "Link" && wideMenuTheme ? 3 : 1}
                                        layer={0}
                                        {...({
                                            handlePageClick,
                                        } as any)}
                                        {...styles.panelMenu}
                                    />
                                );
                            }
                            return (
                                <PanelRow
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    href={link.url}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                    className="main-navigation-row"
                                    onClick={closeDrawer}
                                    {...styles.mainNavigationRow}
                                >
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.mainNavigationRowTypography}
                                    >
                                        {parse(link.title, parserOptions)}
                                    </Typography>
                                </PanelRow>
                            );
                        })}

                    {displayVmiNavigation &&
                        vmiPageLinks.map(
                            link =>
                                link && (
                                    <PanelRow
                                        className="main-navigation-row"
                                        key={link.id}
                                        isCurrent={currentPageUrl === link.url}
                                        onClick={closeDrawer}
                                        href={link.url}
                                        {...styles.mainNavigationRow}
                                    >
                                        <Typography
                                            className="main-navigation-row-text"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                ),
                        )}
                </StyledSection>

                {/*  */}
                {/* Header Link List */}
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {headerLinkListLinks.map((headerLinkItem: any, index) => {
                        let menuHasAtLeastOneNavigationalItem = false;
                        if (headerLinkItem.isMenu && headerLinkItem?.children?.length) {
                            menuHasAtLeastOneNavigationalItem = headerLinkItem.children!.some(
                                (child: { excludeFromNavigation: any }) => !child.excludeFromNavigation,
                            );
                        }
                        if (
                            headerLinkItem.children &&
                            headerLinkItem.children.length > 0 &&
                            headerLinkItem.isMenu === true &&
                            menuHasAtLeastOneNavigationalItem
                        ) {
                            return (
                                <PanelMenu
                                    className="panel-menu"
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    currentUrl={currentPageUrl}
                                    closeOverlay={closeDrawer}
                                    panelTrigger={
                                        <PanelRow
                                            className="main-navigation-row"
                                            hasChildren
                                            {...styles.mainNavigationRow}
                                        >
                                            <Typography
                                                className="main-navigation-row-text"
                                                onClick={(e: any) =>
                                                    handlePageClick(
                                                        e,
                                                        headerLinkItem.url,
                                                        headerLinkItem.overriddenTitle || headerLinkItem.link.title,
                                                    )
                                                }
                                                {...styles.mainNavigationRowTypography}
                                            >
                                                {headerLinkItem.overriddenTitle || headerLinkItem.link.title}
                                            </Typography>
                                        </PanelRow>
                                    }
                                    menuItems={headerLinkItem.children}
                                    layer={0}
                                    {...({
                                        handlePageClick,
                                    } as any)}
                                    {...styles.panelMenu}
                                />
                            );
                        }
                        return (
                            <PanelRow
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                href={headerLinkItem.link.url}
                                target={headerLinkItem.openInNewWindow ? "_blank" : ""}
                                className="main-navigation-row"
                                onClick={closeDrawer}
                                {...styles.mainNavigationRow}
                            >
                                <Typography
                                    className="main-navigation-row-text"
                                    {...styles.mainNavigationRowTypography}
                                >
                                    {headerLinkItem.overriddenTitle || headerLinkItem.link.title}
                                </Typography>
                            </PanelRow>
                        );
                    })}
                </StyledSection>

                {/*  */}
                {/* My Account , My Cart , Language Selector , Currency Selector */}
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {userName && !isGuest && !isPunchOutSession ? (
                        <PanelMenu
                            className="panel-menu"
                            currentUrl={currentPageUrl}
                            panelTrigger={
                                <PanelRow className="main-navigation-row" hasChildren {...styles.mainNavigationRow}>
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.userRowTypography}
                                        onClick={(e: any) =>
                                            handlePageClick(e, myAccountPageLink?.url, translate("My Account"))
                                        }
                                    >
                                        {myAccountPageLink && myAccountPageLink.title
                                            ? myAccountPageLink.title
                                            : translate("My Account")}
                                    </Typography>
                                </PanelRow>
                            }
                            menuItems={accountLinks?.length ? accountLinks : myAccountPageLink?.children}
                            maxDepth={1}
                            closeOverlay={undefined}
                            layer={0}
                            {...({
                                handlePageClick,
                            } as any)}
                            {...styles.panelMenu}
                        />
                    ) : isPunchOutSession ? (
                        <PanelRow className="main-navigation-row" {...styles.mainNavigationRow}>
                            <Typography className="punch-out-user-row-text" {...styles.punchOutUserRowText}>
                                {userName}
                            </Typography>
                        </PanelRow>
                    ) : (
                        <></>
                    )}
                    {isLanguageWidgetAuthored && (
                        <StyledWrapper className="language-selector-wrapper" {...styles.languageSelectorWrapper}>
                            <SelectorMenu
                                dataTestSelector="mobileLanguageSelector"
                                options={languages?.map(l => {
                                    return {
                                        title: l.languageCode.toUpperCase(),
                                        clickableProps: {
                                            onClick: () => {
                                                changeLanguage({ languageId: l.id });
                                                closeDrawer();
                                            },
                                        },
                                    };
                                })}
                                currentOption={currentLanguage?.languageCode}
                                closeModal={closeDrawer}
                                currentOptionIcon={
                                    currentLanguage?.imageFilePath ? (
                                        <LogoImage
                                            className="language-image"
                                            src={currentLanguage.imageFilePath}
                                            alt=""
                                        />
                                    ) : (
                                        <Icon className="menu-row-icon" src={Globe} {...styles.menuRowIcon} />
                                    )
                                }
                            />
                        </StyledWrapper>
                    )}
                    {isCurrencyWidgetAuthored && (
                        <StyledWrapper className="currency-selector-wrapper" {...styles.currencySelectorWrapper}>
                            <SelectorMenu
                                options={currencies?.map(c => {
                                    return {
                                        title: c.currencyCode.toUpperCase(),
                                        clickableProps: {
                                            onClick: () => {
                                                setCurrency({ currencyId: c.id });
                                                closeDrawer();
                                            },
                                        },
                                    };
                                })}
                                closeModal={closeDrawer}
                                currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                                currentOptionIcon={
                                    <Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>
                                }
                            />
                        </StyledWrapper>
                    )}
                    {displayModeSwitch && (
                        <SelectorMenu
                            options={[
                                {
                                    title: translate("Storefront"),
                                    clickableProps: {
                                        onClick: () => {
                                            navigationModeChangeHandler("Storefront");
                                            closeDrawer();
                                        },
                                    },
                                },
                                {
                                    title: translate("Vendor Managed Inventory"),
                                    clickableProps: {
                                        onClick: () => {
                                            navigationModeChangeHandler("Vmi");
                                            closeDrawer();
                                        },
                                    },
                                },
                            ]}
                            closeModal={closeDrawer}
                            currentOption={
                                currentMode === "Vmi" ? translate("Vendor Managed Inventory") : translate("Storefront")
                            }
                        />
                    )}
                </StyledSection>

                {/*  */}
                {/* Sign Out */}
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {userName && !isGuest && (
                        <PanelRow className="sign-out-row" onClick={onSignOutHandler} {...styles.signOutRow}>
                            <StyledSpan
                                css={css`
                                    display: flex;
                                    align-items: center;
                                `}
                            >
                                <Typography className="sign-out-row-text" {...styles.signOutRowText}>
                                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                                </Typography>
                                <Icon {...styles.signOutIcon}></Icon>
                            </StyledSpan>
                        </PanelRow>
                    )}
                </StyledSection>
            </Drawer>
        </>
    );
};

const LogoImage = styled.img`
    margin-right: 5px;
    height: 18px;
`;

interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption?: string;
    currentOptionIcon?: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow hasChildren className="selector-menu" {...styles.mainNavigationRow}>
            <StyledSpan>
                {currentOptionIcon}
                <Typography
                    className="main-navigation-row-text selector-menu-title"
                    data-test-selector={`${dataTestSelector}_currentOption`}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            className="panel-menu selector-panel-menu"
            currentUrl={currentOption}
            data-test-selector={dataTestSelector}
            closeOverlay={undefined}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};

function findVisibilityState(data: any): string | undefined {
    for (const key in data) {
        if (data[key].type === "Common/HeaderSignIn") {
            return data[key].generalFields.visibilityState;
        }
    }
    return undefined;
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(NavigationDrawer)));
